import React from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useTranslation } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { Milestone, CheckCircle2, Circle, Rocket, ShieldCheck, Globe, Database } from "lucide-react";
const ROADMAP_ITEMS = [
  {
    phase: "phase_security",
    status: "Live",
    icon: ShieldCheck,
    items: [
      { title: "Clerk Auth Integration", desc: "Enterprise-grade SSO/OIDC and TOTP native support." },
      { title: "PII Masking v1", desc: "Regex-based redaction for PII in all logs and LLM IO." }
    ]
  },
  {
    phase: "phase_governance",
    status: "Beta",
    icon: Database,
    items: [
      { title: "D1 Audit Hub", desc: "Immutable storage for every system interaction and agent run." },
      { title: "EU AI Act Scorer", desc: "Real-time compliance checking against EU governance standards." }
    ]
  },
  {
    phase: "phase_scalability",
    status: "Planned",
    icon: Globe,
    items: [
      { title: "Multi-tenant Clusters", desc: "Isolated compute resources per workspace with shared registry." },
      { title: "Durable Object Quotas", desc: "Granular control over storage and request limits." }
    ]
  },
  {
    phase: "Scalability & Billing",
    status: "Planned",
    icon: Rocket,
    items: [
      { title: "Usage-based Billing", desc: "Stripe integration for per-token and per-call invoicing." },
      { title: "OAuth Connect", desc: "Enable third-party apps to access Cloudface agents securely." }
    ]
  }
];
export default function EnterpriseRoadmapPage() {
  const { t } = useTranslation();
  return (
    <AppLayout container>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-12">
          <div className="p-3 rounded-2xl bg-primary/10 text-primary">
            <Milestone className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight">{t('roadmap_title')}</h1>
            <p className="text-muted-foreground mt-2">Our vision for the next generation of enterprise AI governance.</p>
          </div>
        </div>
        <div className="relative space-y-12 before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-muted-foreground/20 before:to-transparent">
          {ROADMAP_ITEMS.map((section, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-full border bg-background shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                {section.status === 'Live' ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : section.status === 'Beta' ? (
                  <Rocket className="w-5 h-5 text-primary" />
                ) : (
                  <Circle className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 rounded-2xl border bg-card/50 backdrop-blur-sm shadow-sm transition-all hover:shadow-md hover:border-primary/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <section.icon className="w-5 h-5 text-primary" />
                    <h2 className="font-bold text-lg">{t(section.phase as any) || section.phase}</h2>
                  </div>
                  <Badge variant={section.status === 'Live' ? 'default' : section.status === 'Beta' ? 'secondary' : 'outline'}>
                    {section.status}
                  </Badge>
                </div>
                <div className="space-y-4">
                  {section.items.map((item, i) => (
                    <div key={i} className="space-y-1">
                      <p className="font-semibold text-sm">{item.title}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
